import {
  Avatar,
  AvatarSize,
  AvatarStyle,
  Badge,
  BadgeSize,
  ButtonDisplayMode,
  ButtonHierarchy,
  ButtonShape,
  ButtonSize,
  MainButton,
  Typography,
  TypographySize,
  TypographyWeight,
} from "@beydesign/storybook"
import { Box, Divider } from "@mui/material"
import React, { useState } from "react"
import { Agent } from "../../types/Agent"
import { SnackbarType } from "../../types/UI"
import { getAgentChatUrl } from "../../utils/func"
import { SnackbarWithText } from "../snackbar"

interface ShareModalProps {
  visible: boolean
  onClose: () => void
  agent: Agent
  agentImageUrl: string
}

export const ShareModal: React.FC<ShareModalProps> = ({
  agent,
  agentImageUrl,
  onClose,
  visible,
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false)

  const handleCopyLink = () => {
    const url = getAgentChatUrl(agent.id, agent.custom_branding)
    navigator.clipboard.writeText(url)
    setSnackbarOpen(true)
    setTimeout(() => onClose(), 2000)
  }

  if (!visible) return null

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        zIndex: 100,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(78, 64, 90, 0.40)",
      }}
    >
      <Box
        sx={{
          width: "480px",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "var(--colors-light-background-bg-white)",
          padding: "1.5rem",
          borderRadius: "1rem",
          position: "relative",
        }}
      >
        <SnackbarWithText
          text="Conversation link copied"
          open={snackbarOpen}
          handleClose={() => setSnackbarOpen(false)}
          severity={SnackbarType.success}
        />

        <Box sx={{ position: "absolute", top: "0.8rem", right: "0.8rem" }}>
          <MainButton
            onClick={onClose}
            displayIconMode={ButtonDisplayMode.Only}
            displayIcon="X"
            shape={ButtonShape.Circle}
            hierarchy={ButtonHierarchy.Tetriary}
            size={ButtonSize.md}
            text="Close"
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            text="Conversation with"
            size={TypographySize.HeadingXS}
            weight={TypographyWeight.Bold}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              paddingX: "2rem",
            }}
          >
            <Avatar
              size={AvatarSize.md}
              text={agent.name}
              avatarStyle={
                agentImageUrl ? AvatarStyle.Picture : AvatarStyle.Letters
              }
              image={agentImageUrl}
            />
            <Box
              display="flex"
              flexDirection="column"
              alignItems="start"
              sx={{ marginLeft: "1rem" }}
              gap={"4px"}
            >
              <Typography
                text={agent.name}
                size={TypographySize.TextS}
                weight={TypographyWeight.SemiBold}
              />
              <Badge text={agent?.mode || "N/A"} size={BadgeSize.xs} />
            </Box>
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        {!agent.active && (
          <Typography
            text="Please activate your agent to share the conversation link"
            size={TypographySize.TextXS}
            weight={TypographyWeight.Regular}
            color="var(--primitives-desktop-primary-gray-700)"
            className="text-center"
          />
        )}
        <MainButton
          onClick={handleCopyLink}
          style={{ marginTop: "0.5rem" }}
          size={ButtonSize.md}
          text="Copy Conversation link"
          showTrailingIcon
          trailingIcon="Link"
          disabled={!agent.active}
        />
      </Box>
    </Box>
  )
}
